import { useState, useEffect, useMemo } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, SubHeader, Msg, StyledInput, Label, Toggle, PrivacyPicker } from '../../components/Leagues/ui'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import GradientButton from '../../components/GradientButton'

export default function SezioneImpostazioni({ leagueId, isAdmin, leagueName, leagueIsPublic, onSettingsChange }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [settings, setSettings] = useState({ autopick: false, team_race: false, team_member: 2, average: false, flip: false })
  const [loading, setLoading]   = useState(true)
  const [name, setName]         = useState(leagueName ?? '')
  const [password, setPassword] = useState('')
  const [saving, setSaving]     = useState(false)
  const [msg, setMsg]           = useState(null)

  useEffect(() => {
    if (!leagueId) return
    supabase.from('dim_league').select('autopick,team_race,team_member,average,flip,name,password').eq('id_league', leagueId).single()
      .then(({ data }) => {
        if (data) {
          setSettings({ autopick: !!data.autopick, team_race: !!data.team_race, team_member: data.team_member || 2, average: !!data.average, flip: !!data.flip })
          setName(data.name ?? ''); setPassword(data.password ?? '')
        }
        setLoading(false)
      })
  }, [leagueId])

  const update = async (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }))
    await supabase.from('dim_league').update({ [field]: value }).eq('id_league', leagueId)
  }

  const handleSaveMeta = async () => {
    if (!name.trim()) { setMsg({ type: 'error', text: t('league.err_name_empty') }); return }
    setSaving(true); setMsg(null)
    try {
      await supabase.from('dim_league').update({ name: name.trim(), password: password.trim() || null }).eq('id_league', leagueId)
      setMsg({ type: 'success', text: t('league.settings_saved') })
      onSettingsChange?.({ name: name.trim() })
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setSaving(false) }
  }

  if (!isAdmin) return null

  const TeamSizeOptions = [2, 3, 4, 5, 6]

  return (
    <View style={{ gap: 12, marginBottom: 4 }}>
      <Card style={{ marginBottom: 0 }}>
        <SubHeader icon={<Ionicons name="settings-outline" size={16} color={tc.rosaText} />}>{t('league.settings_section')}</SubHeader>
        {msg && <Msg type={msg.type} text={msg.text} />}
        <Label>{t('league.settings_name')}</Label>
        <StyledInput value={name} onChangeText={setName} placeholder={t('league.create_name_placeholder')}  />
        <Label>{`Password${leagueIsPublic ? ` (${t('common.optional')})` : ''}`}</Label>
        <StyledInput value={password} onChangeText={setPassword} placeholder={t('league.pwd_public_placeholder')} secureTextEntry />
        <GradientButton onPress={handleSaveMeta} loading={saving}>{t('league.settings_save')}</GradientButton>
      </Card>

      {[
        { field: 'autopick',       icon: <MaterialCommunityIcons name='robot-outline' size={16} color={tc.rosaText} />, title: t('league.settings_autopick'),       desc: t('league.settings_autopick_desc') },
        { field: 'flip',           icon: <MaterialCommunityIcons name='shuffle-variant' size={16} color={tc.rosaText} />, title: t('league.settings_flip'),           desc: t('league.settings_flip_desc') },
        { field: 'team_race',      icon: <MaterialCommunityIcons name='shield-check-outline' size={16} color={tc.rosaText} />, title: t('league.settings_team_race'),      desc: t('league.settings_team_race_desc') },
      ].map(({ field, icon, title, desc }) => (
        <Card key={field} style={{ marginBottom: 0 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <View style={{ flex: 1 }}>
              <SubHeader icon={icon}>{title}</SubHeader>
              <Text style={[styles.settingDesc, { color: tc.textMuted }]}>{desc}</Text>
            </View>
            <Toggle active={settings[field]} onToggle={() => update(field, !settings[field])} disabled={loading} />
          </View>
          {field === 'team_race' && settings.team_race && (
            <View style={[styles.teamExtra, { borderTopColor: tc.border }]}>
              <Text style={[styles.teamExtraLabel, { color: tc.rosaText }]}>{t('league.settings_team_size')}</Text>
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                {TeamSizeOptions.map(n => (
                  <TouchableOpacity key={n} onPress={() => update('team_member', n)} accessibilityRole="button" accessibilityState={{ selected: settings.team_member === n }} style={[styles.teamSizeBtn, { backgroundColor: settings.team_member === n ? tc.rosa : tc.bgCard2, borderColor: settings.team_member === n ? tc.rosa : tc.border }]}>
                    <Text style={[styles.teamSizeBtnText, { color: settings.team_member === n ? tc.brandText : tc.textMuted }]}>{n}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={[styles.teamExtraLabel, { color: tc.rosaText, marginTop: 12 }]}>{t('league.settings_scoring')}</Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {[{ v: false, l: t('league.settings_scoring_best2') }, { v: true, l: t('league.settings_scoring_avg') }].map(o => (
                  <TouchableOpacity key={String(o.v)} onPress={() => update('average', o.v)} accessibilityRole="button" accessibilityLabel={o.l} accessibilityState={{ selected: settings.average === o.v }} style={[styles.teamSizeBtn, { flex: 1, backgroundColor: settings.average === o.v ? tc.rosa : tc.bgCard2, borderColor: settings.average === o.v ? tc.rosa : tc.border }]}>
                    <Text style={[styles.teamSizeBtnText, { color: settings.average === o.v ? tc.brandText : tc.textMuted, fontSize: 12 }]}>{o.l}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}
        </Card>
      ))}
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  settingDesc:    { fontSize: 12, lineHeight: 18, marginTop: -8 },
  teamExtra:      { marginTop: 16, paddingTop: 16, borderTopWidth: 1, gap: 6 },
  teamExtraLabel: { fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  teamSizeBtn:    { borderRadius: 8, borderWidth: 1, paddingHorizontal: 12, minHeight: 44, minWidth: 44, justifyContent: 'center', alignItems: 'center' },
  teamSizeBtnText:{ fontFamily: fonts.condensedBold, fontSize: 13 },
})